import { MapContainer, TileLayer, Marker, Popup, Circle } from 'react-leaflet';
import { Link } from 'react-router-dom';
import type { GeoFix } from '../api/hooks';
import { effectiveFix } from '../api/hooks';
import { addressWithoutVenue, humanisePlaceType } from './format';
import { locationConfidence } from '../lib/locationConfidence';
import type { ConfidenceTone } from '../lib/locationConfidence';

// Tone → class. `warn` is reserved for states an operator should act on; a fix
// that is merely old because the device was switched off is `muted`, not a
// warning (DE-281 — presenting the two identically is what sent an operator
// chasing a geolocation fault that did not exist).
const TONE_CLASS: Record<ConfidenceTone, string> = {
  ok: 'text-muted',
  muted: 'text-muted',
  warn: 'text-warn',
};

const OSM_TILES = 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png';
// Geoapify credit required wherever its results are shown (DE-170).
const OSM_ATTR =
  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors' +
  ' · Addresses <a href="https://www.geoapify.com/">Geoapify</a>';

export function DeviceLocationCard({
  geo,
  thing,
  online,
  lastSeen,
}: {
  geo: GeoFix | null;
  thing?: string;
  // DE-281 — presence context. Without it a stale fix cannot be told apart
  // from a device that was simply powered off, so the card would either cry
  // wolf or stay silent about a genuine scan-pipeline fault.
  online?: boolean;
  lastSeen?: string | null;
}) {
  const fix = effectiveFix(geo);
  const confidence = locationConfidence({ geo, online, lastSeen });
  if (!fix) {
    return (
      <section className="card">
        <div className="flex items-baseline justify-between mb-2">
          <h2 className="font-medium">Location</h2>
          {thing && (
            <Link
              to={`/devices/${encodeURIComponent(thing)}/locations`}
              className="text-xs text-accent hover:bg-soft border border-border rounded-md px-2 py-1 whitespace-nowrap"
            >
              View location history →
            </Link>
          )}
        </div>
        <p className="text-sm text-muted">No location reported.</p>
      </section>
    );
  }
  const accuracy = fix.accuracy;
  // DE-162: current resolution failed but we have a historical fix — render the
  // pin faded. The wording that goes with it now comes from locationConfidence
  // (DE-281), which covers the healthy states too.
  const isUnverified = !fix.verified;
  const ringOpacity = isUnverified ? 0.4 : 0.85;
  const ringColor = isUnverified ? '#94a3b8' : '#2563eb';
  const circleDashArray = isUnverified ? '4 4' : undefined;
  // Geoapify's address usually leads with the venue name; drop the repeat.
  const addr = addressWithoutVenue(fix.address, fix.place_name);
  // A venue with no name of its own still has a kind worth showing —
  // "Conference centre" beats a bare street for an operator, and beats
  // borrowing a neighbouring building's proper noun.
  const venue = fix.place_name ?? humanisePlaceType(fix.place_type);

  return (
    <section className="card">
      <div className="flex items-baseline justify-between mb-2">
        <h2 className="font-medium">Location</h2>
        {thing && (
          // Promoted from a plain 11px text link to a bordered control: as a
          // bare link in the header corner it read as a caption and operators
          // reported being unable to reach the history page at all.
          <Link
            to={`/devices/${encodeURIComponent(thing)}/locations`}
            className="text-xs text-accent hover:bg-soft border border-border rounded-md px-2 py-1 whitespace-nowrap"
          >
            View location history →
          </Link>
        )}
      </div>
      {/*
        DE-281 — the confidence line is shown on EVERY path, not just failures.
        Before this the healthy path rendered nothing but fmtAgo(geo.timestamp),
        which is the "position established at" date, not a freshness signal: a
        device re-confirmed two minutes ago advertised itself as 53 days old.
      */}
      <p className={`text-[11px] mb-2 ${TONE_CLASS[confidence.tone]}`} title={confidence.explanation}>
        {confidence.headline}
        {confidence.evidence.length > 0 && (
          <span className="text-muted"> · {confidence.evidence.join(' · ')}</span>
        )}
      </p>
      <div className="rounded-md overflow-hidden border border-border">
        {/*
          react-leaflet's MapContainer treats `center` and `zoom` as initial-
          mount props only — they aren't re-applied on prop changes. Keying
          the container by lat,lon forces a remount when the coords change,
          which re-applies the new center.
        */}
        <MapContainer
          key={`${fix.lat.toFixed(5)},${fix.lon.toFixed(5)}`}
          center={[fix.lat, fix.lon]}
          zoom={15}
          scrollWheelZoom={false}
          style={{ height: 280, width: '100%' }}
        >
          <TileLayer url={OSM_TILES} attribution={OSM_ATTR} />
          <Marker
            position={[fix.lat, fix.lon]}
            opacity={isUnverified ? 0.5 : 1}
          >
            <Popup>
              <div className="text-xs">
                {thing && <div className="font-mono">{thing}</div>}
                {venue && (
                  <div className={fix.place_name ? 'font-medium' : 'font-medium text-muted'}>
                    {venue}
                  </div>
                )}
                {addr && <div>{addr}</div>}
                <div>lat {fix.lat.toFixed(5)}, lon {fix.lon.toFixed(5)}</div>
                <div>accuracy {accuracy ? `${Math.round(accuracy)} m` : '—'}</div>
                {fix.source && <div>source {fix.source}</div>}
                <div className={TONE_CLASS[confidence.tone]}>{confidence.headline}</div>
                {confidence.evidence.map((e) => (
                  <div key={e} className="text-muted">{e}</div>
                ))}
                {confidence.established && (
                  <div className="text-muted">{confidence.established}</div>
                )}
              </div>
            </Popup>
          </Marker>
          {accuracy > 0 && (
            <Circle
              center={[fix.lat, fix.lon]}
              radius={accuracy}
              pathOptions={{
                color: ringColor,
                weight: 1,
                fillOpacity: 0.08 * ringOpacity / 0.85,
                dashArray: circleDashArray,
              }}
            />
          )}
        </MapContainer>
      </div>
      {/* DE-170: the human-readable answer gets its own line above the
          coordinate strip — an address is the thing an operator reads first,
          and it's too long to sit in the flex row without wrapping badly. */}
      {(venue || addr) && (
        <div className="mt-2 text-xs">
          {venue && (
            <div className={fix.place_name ? 'font-medium' : 'font-medium text-muted'}>
              {venue}
            </div>
          )}
          {addr && <div className={venue ? 'text-muted' : 'text-fg'}>{addr}</div>}
        </div>
      )}
      <div className="mt-2 text-xs text-muted flex gap-3">
        <span className="tabular-nums">
          {fix.lat.toFixed(5)}, {fix.lon.toFixed(5)}
        </span>
        <span>±{accuracy ? Math.round(accuracy) : '?'} m</span>
        {fix.source && <span>{fix.source}</span>}
        {confidence.established && <span>{confidence.established}</span>}
      </div>
    </section>
  );
}
